/**
 * ENHANCED TERRAIN — the decoration layer, and the only part of this lane's
 * work behind a setting.
 *
 * The rolling ground itself (`packages/engines flight/terrain-relief.ts`) is not here: it feeds
 * the sim's `GroundQuery` and so has to be identical for every peer, replay and
 * census. Everything in *this* module is something nothing can collide with, so
 * a player can switch it off and get today's pixels back exactly.
 *
 * Three things, in descending order of how well the data supports them. That
 * ordering is the honest part and it is worth stating plainly rather than
 * burying: only the first is anchored to a real object.
 *
 * ### 1. Copses — anchored to shipped placements, and standing *in place of* them
 *
 * The game places one tree object 19-40 times per region — a polygon trunk
 * carrying three type-1 SPHERE nodes, which its renderer draws as flat filled
 * discs (AD-019 §2.2; `tree-geometry.ts` for the measurements). Each placement
 * becomes a copse of instanced trees at the same position, inside the same
 * crown envelope, in the same four `BARON.PAL` entries — and the copse's first
 * tree stands exactly on the original's trunk.
 *
 * With the toggle on, the discs those trees replace are **hidden**
 * (`setCanopyDiscsVisible`, wired from `flight/scene-terrain.ts`), because
 * drawing both is drawing the same tree twice. With the toggle off nothing
 * here runs and the discs are the only vegetation, exactly as before.
 *
 * **What is measured and what is invented.** Measured: the placement
 * positions, the crown radius and centre height, the four palette entries, the
 * object's 47 x 20 m extent. Invented: *the shape* — lobe and skirt
 * arrangement, trunk taper, facet counts, which surface is lit; the nine trees
 * a copse is made of and the 23 m they are scattered over; and everything in
 * §2 below. An earlier version of this header claimed "nothing is invented but
 * the shape", which was never true of §2 and is not true of the copse count
 * either. AD-019 §5.6 called that out; this is the correction.
 *
 * ### 2. Woods — derived from landcover, by an interpretation, and wholly invented as objects
 *
 * Each region carries one large landcover patchwork object — Verdun's
 * `TERR5-72` is 43 polygons over 42 x 40 km in nine palette entries, London's
 * `TERR1-71` is 89 polygons in sixteen. Those entries split cleanly into browns
 * (128, 130, 137-142 — ploughed earth) and greens (132 `#616d10`, 133
 * `#516534`, 134 `#556134`, 143 `#4d5928`).
 *
 * **Reading the green entries as woodland and pasture is an interpretation, not
 * a decode.** The data says "this polygon is this colour"; it does not say what
 * grows there. What makes the reading defensible is that the shipped tree
 * object's own canopies are painted **133 and 134** — two of those same green
 * entries — so the game itself paints its trees the colour it paints that
 * ground. Woods are therefore scattered only inside polygons whose colour the
 * game already uses for foliage, never on the ploughed browns.
 *
 * That is where the evidence stops. **A wood is a port invention end to end**:
 * the original places no tree anywhere except its 19-40 shipped copses, so
 * every one of the up-to-400 sites of 26 trees here is vegetation the game
 * never had. The landcover colour picks *where* it is plausible to put one; it
 * does not say one is there. Only the tree's own dimensions are measured.
 *
 * ### 3. Water and field tone — designed, NOT BUILT
 *
 * Two further treatments belong under this toggle and are deliberately absent
 * rather than half-done, because both turned out to be more than the
 * vertex-colour rewrites they look like:
 *
 * - **Moving water.** `WATER_RAMP` (`world/terrain-palette.ts`) already
 *   recovers the game's own blue run 1-7 with the measured river/sea colour at
 *   its centre, so a swell quantised through it would stay in palette. What
 *   stops it is plumbing, not colour: water faces live inside the same shared
 *   geometry as every other face of their object, so animating them means
 *   splitting them into their own mesh with its own node material. That is the
 *   first thing to build next, and it is what the Channel needs.
 * - **Field parcels.** The landcover plates are *subdivided* already, by
 *   `terrain-drape.ts`, so the mesh to carry per-parcel tone exists — but
 *   choosing tones is a design question (how much variation before a quilt
 *   stops reading as farmland) that wants a picture to argue over first.
 *
 * ## Budget, and why it is tiles at all — with the price now measured
 *
 * This used to be four `InstancedMesh`es, one per variant, each covering the
 * whole region — and that is a trap disguised as a good number. three.js culls
 * per *object*, so a mesh whose bounding sphere spans 42 km is never culled:
 * every instance in the region was submitted every frame no matter where the
 * camera pointed, and the vertex shader threw most of them away after
 * transforming them. "Four draw calls" was true and beside the point.
 *
 * Instances are therefore bucketed by variant **and by a `TILE_M` grid square**,
 * so each mesh gets a tight bounding sphere and the frustum test can do real
 * work. Culling on a tile boundary cannot pop: three.js drops a mesh only when
 * its bounding sphere is wholly outside the frustum, so nothing visible is ever
 * removed.
 *
 * **What the tiling costs was measured in 2026-08-27 and what it buys was not,
 * and that asymmetry is what set `TILE_M` at 8,192 for four months.** The cost
 * is pipelines: three writes an `InstancedMesh`'s instance count into its
 * shader text, so *each tile is its own WGSL and its own `GPURenderPipeline`*,
 * and 8,192 m tiles put 80 tree meshes on Verdun where 32,768 m tiles put a
 * handful. The 2026-08-31 lane measured the other column on a real Blackwell
 * with `apps/web/scripts/frame-cost.ts` — free-running `requestAnimationFrame`,
 * a paused sortie, 400 frames a sample, eight poses in two scenes — and the
 * answer is that **the tiling does not pay for itself in a frame**:
 *
 * | | 8,192 | 32,768 |
 * |---|---|---|
 * | pipelines inside `precompile` (Verdun) | **154** | **99** |
 * | WGSL compiled | 371 KB | 278 KB |
 * | mean frame, worst pose | 1.19 ms | 1.19 ms |
 * | mean frame, every other pose | 0.43–1.34 ms | 0.39–1.40 ms |
 * | draws/frame, worst pose | **75** | **67** |
 *
 * Every frame-time cell is inside the run-to-run spread of its neighbour, and
 * the draw count goes the *wrong way for the tiling*: a forward view over a
 * 42 km region holds most of the tiles anyway, so cutting them small mostly
 * produces more draw calls rather than more culling. And the picture is not a
 * variable at all — `apps/web/scripts/pose-sweep.ts` across the change gives
 * **8 of 8 poses byte-identical** on a ground start and the three propeller
 * poses inside their own same-configuration control floor elsewhere, which is
 * what culling correctness looks like when it is stated as pixels.
 *
 * So the constant is 32,768: 55 fewer pipelines off every boot, no measurable
 * frame cost, same picture. The honest caveat is that a Blackwell renders this
 * whole scene in about a millisecond, so this box cannot see the harm the
 * tiling was built to prevent — if a weak-GPU report ever names the trees, the
 * number to move back is this one, and `frame-cost.ts` is how to argue about
 * it.
 *
 * ## A tile is capped in *instances* as well as metres — and metres alone was
 * not enough
 *
 * `TILE_M` says how much ground a mesh covers. It says nothing about how many
 * trees stand on that ground, and **the number of instances in an
 * `InstancedMesh` decides which of two entirely different vertex paths three
 * r185 compiles it onto** (`nodes/accessors/Instance.js`, `createInstanceMatrixNode`):
 *
 * ```js
 * const uniformBufferSize = matrixCount * 16 * 4;
 * if ( uniformBufferSize <= builder.getUniformBufferLimit() ) {
 *     instanceMatrixNode = buffer( instanceMatrix.array, 'mat4', matrixCount )…   // uniform array
 * } else {
 *     …new InstancedInterleavedBuffer( instanceMatrix.array, 16, 1 )…             // 4 × vec4 instanced attributes
 * }
 * ```
 *
 * `getUniformBufferLimit()` is `device.limits.maxUniformBufferBindingSize`, and
 * three asks for its device with `requiredLimits: {}`
 * (`WebGPUBackend.js`), so every conformant browser answers with the WebGPU
 * **default, 65,536 bytes** — i.e. the switch flips at exactly
 * `UNIFORM_INSTANCE_LIMIT` = 1,024 matrices, on every machine, not just slow
 * ones.
 *
 * Raising `TILE_M` to 32,768 pushed four of London's tree meshes to **1,750–1,758
 * instances** and four of Paris's to 1,041–1,084 — over the line — while
 * Verdun (721), Somme (446) and Dunkirk (638) stayed under it. Censused against
 * the real bake, at 8,192 **no** mesh in any region crossed it. So G57 silently
 * moved 7,020 of London's 10,571 trees, and none of any other region's, onto a
 * vertex path they had never been compiled on before; the user's 2026-09-01
 * report is "London: I see buildings but no trees", on Safari 26.5 / Apple GPU,
 * where nothing else in this app takes that path (every other `InstancedMesh`
 * the port builds is a few hundred instances at most). That is a coincidence
 * this file should not be asking anyone to accept.
 *
 * **So a bucket is split when it is full, at `UNIFORM_INSTANCE_LIMIT`.** The
 * cap is a constant rather than a device query on purpose: it is the *floor*
 * every conformant device guarantees, so the meshes this builds are on the same
 * path everywhere — which is what makes the picture on the reference machine
 * evidence about the picture on his. A device that reports a larger limit is
 * still perfectly happy with 1,024.
 *
 * It costs almost nothing, because only two regions have a bucket to split:
 * London 32 → 36 meshes and Paris 28 → 32, with Verdun, Somme and Dunkirk
 * unchanged at 44/40/44. G57's pipeline win — measured on Verdun, 154 → 99 — is
 * therefore untouched, and the tiling and the cap are orthogonal: the tile still
 * decides what is culled together, the cap only decides how many meshes a full
 * tile is drawn as.
 *
 * The honest limit of this: **it is a fix to a mechanism, not to a reproduction.**
 * Nobody on this project has a Safari to point at, and on Chromium/Blackwell
 * both paths render identically (the pose sweep across G57 was 8 of 8
 * byte-identical). What is proven is that the change put London's trees, alone
 * in the whole app, on a path they had never been on; what is assumed is that
 * WebKit's handling of an instance-stepped interleaved vertex buffer is where
 * they went. `?boot-report`'s `pipeline errors` line (`flight/boot-report.ts`)
 * exists so his next report answers that instead of us inferring it.
 *
 * All materials opaque, depth buffer untouched, no `renderOrder`, no alpha —
 * lessons.md 2026-08-19 and 2026-08-22.
 *
 * ## What is next door
 *
 * **Where** a copse or a wood goes, and how the `MAX_WOOD_SITES` cap is spread
 * over a region's green triangles, is `wood-siting.ts` — pure arithmetic with no
 * three.js in it, split off at the 400-line ceiling. This file is the renderer
 * half: it takes those sites and decides what meshes they become.
 */

import * as THREE from "three/webgpu";
import { createTreeVariants, type TreeVariant } from "./tree-geometry.ts";
import type { GroundSampler } from "./terrain-drape.ts";
import { hash01, type TreeSite } from "./wood-siting.ts";

/**
 * Side of a bucketing tile, metres. A region is 42 x 40 km, so this is a 2 x 2
 * grid — the module doc has the frame-cost and pipeline tables that chose it
 * over 8,192, and the instrument that would revisit it.
 */
export const TILE_M = 32768;

/**
 * Most instances one `InstancedMesh` this module builds is allowed to hold.
 *
 * `maxUniformBufferBindingSize`'s WebGPU default is 65,536 bytes and an
 * instance matrix is 64 of them, so this is the largest count three r185 will
 * still put in a uniform array rather than in an instance-stepped interleaved
 * vertex buffer — the module doc has the source and the census that made it
 * matter. A **floor**, not a query: every conformant device allows at least
 * this, so capping here puts every tree mesh on the same vertex path on every
 * machine.
 */
export const UNIFORM_INSTANCE_LIMIT = 1024;

/**
 * A variant-and-tile bucket key. Tile indices can be negative, so they are
 * stringified rather than packed.
 *
 * The tile is chosen from the **site's** centre, not from each tree's own
 * position. Bucketing per tree splits every site that happens to straddle a
 * grid line across up to four tiles — a site on the origin produced fourteen
 * near-empty meshes instead of four. A site is at most `WOOD_RADIUS_M` across,
 * so keeping it whole overhangs its tile by 130 m out of 8 192 and costs the
 * cull nothing; the bounding spheres are computed from the instances anyway.
 */
function bucketKey(variant: number, siteX: number, siteZ: number): string {
  return `${variant}|${Math.floor(siteX / TILE_M)}|${Math.floor(siteZ / TILE_M)}`;
}

/**
 * Scatters `site.count` trees over a disc, deterministically from the site's
 * own position, into per-variant-per-tile buckets.
 *
 * Tree 0 sits exactly on the site centre and is not scattered. For a copse
 * that centre is the original object's own trunk, so the enhanced tree stands
 * where the game's tree stands rather than beside it — which is what makes
 * hiding the disc a replacement instead of a deletion.
 */
function scatterSite(
  site: TreeSite,
  index: number,
  sample: GroundSampler,
  out: Map<string, THREE.Matrix4[]>,
  variants: number,
): void {
  const matrix = new THREE.Matrix4();
  const scaleVector = new THREE.Vector3();
  for (let i = 0; i < site.count; i++) {
    const seed = index * 977 + i;
    // sqrt keeps the disc evenly covered instead of clumping at the middle.
    const r = i === 0 ? 0 : Math.sqrt(hash01(seed, 1)) * site.radiusM;
    const a = hash01(seed, 2) * Math.PI * 2;
    const x = site.x + Math.cos(a) * r;
    const z = site.z + Math.sin(a) * r;
    const scale = 0.75 + hash01(seed, 3) * 0.6;
    matrix.makeRotationY(hash01(seed, 4) * Math.PI * 2);
    matrix.scale(scaleVector.set(scale, scale, scale));
    matrix.setPosition(x, sample(x, z), z);
    const key = bucketKey(Math.floor(hash01(seed, 5) * variants) % variants, site.x, site.z);
    const bucket = out.get(key);
    if (bucket) bucket.push(matrix.clone());
    else out.set(key, [matrix.clone()]);
  }
}

export interface TerrainDecoration {
  readonly group: THREE.Group;
  /** Meshes built. Every one is frustum-culled on its own tile, so the *drawn* count is lower in flight. */
  readonly drawCallCount: number;
  /** Triangles across every instance, if none were culled — the worst case, and the one worth pinning. */
  readonly triangleCount: number;
  readonly treeCount: number;
  readonly copseCount: number;
  readonly woodCount: number;
  readonly dispose: () => void;
}

/**
 * Builds every tree in the region as one `InstancedMesh` per variant per
 * populated tile. Callers add or remove `group` to switch the decoration;
 * nothing here is rebuilt on a toggle, which is what makes flipping it free.
 */
export function buildTerrainDecoration(
  copseSites: readonly TreeSite[],
  woodSites: readonly TreeSite[],
  sample: GroundSampler,
  /** The hour's walk down the canopy run — `flight/time-of-day.ts`'s `groundRampShiftFor`. */
  rampShift = 0,
): TerrainDecoration {
  const variants: TreeVariant[] = createTreeVariants(rampShift);
  const buckets = new Map<string, THREE.Matrix4[]>();

  copseSites.forEach((site, i) => scatterSite(site, i, sample, buckets, variants.length));
  woodSites.forEach((site, i) => scatterSite(site, i + 100_000, sample, buckets, variants.length));

  const group = new THREE.Group();
  const material = new THREE.MeshBasicMaterial({ vertexColors: true });
  const meshes: THREE.InstancedMesh[] = [];
  let treeCount = 0;
  let triangleCount = 0;

  // Sorted, so a region's meshes are added in the same order on every run —
  // `Map` iteration follows insertion, and insertion here follows a hash.
  for (const key of [...buckets.keys()].sort()) {
    const matrices = buckets.get(key)!;
    const variant = variants[Number(key.slice(0, key.indexOf("|")))]!;
    // A tile that holds more trees than a uniform array can carry is drawn as
    // several meshes rather than one — the module doc has the threshold, the
    // census and why metres alone were the wrong unit. The chunks keep the
    // bucket's own order, so which tree lands in which mesh is as deterministic
    // as the bucketing that produced it.
    for (let from = 0; from < matrices.length; from += UNIFORM_INSTANCE_LIMIT) {
      const chunk = matrices.slice(from, from + UNIFORM_INSTANCE_LIMIT);
      const mesh = new THREE.InstancedMesh(variant.geometry, material, chunk.length);
      chunk.forEach((m, i) => mesh.setMatrixAt(i, m));
      mesh.instanceMatrix.needsUpdate = true;
      // The tight per-tile sphere this yields is the entire point of bucketing.
      mesh.computeBoundingSphere();
      group.add(mesh);
      meshes.push(mesh);
      treeCount += chunk.length;
      triangleCount += chunk.length * variant.triangleCount;
    }
  }

  return {
    group,
    drawCallCount: meshes.length,
    triangleCount,
    treeCount,
    copseCount: copseSites.length,
    woodCount: woodSites.length,
    dispose: () => {
      // The four variant geometries are *shared* across tiles, so they are
      // disposed once from the variant list — walking `meshes` would dispose
      // each of them a tileful of times.
      for (const variant of variants) variant.geometry.dispose();
      // One shared material across every variant — disposed once, and disposing
      // it is what frees the render objects (lessons.md 2026-08-19, rule 5).
      material.dispose();
    },
  };
}
